const { useState, useMemo, useCallback } = wp.element;
import { NekoButton, NekoSelect, NekoOption, NekoIcon } from '@neko-ui';
import useDatabaseCleanerContext, { countBusyValue, deleteBusyValue,
	cleanOptionKey, cleanStyles } from './../context';
import { sortIgnoreItemToEnd, takeNap } from './../helper';
import { ItemInfoIcon, NameField, UsedByField } from './common';

const sections = {
	posts: 'Posts',
	postsMetadata: 'Posts Metadata',
	users: 'Users',
	comments: 'Comments',
	transients: 'Transients',
	postTypes: 'Post Types'
}

export const useTableData = ({ bulkTasks, initialList, initialCounts, initialUsedBy,
	mutateList, onUpdateCounts, usedbyKey, section }) => {
	const { busyAction, bulkBatchSize, busyBulkTasks, busyCleanBulkTasks, plugins, delay } = useDatabaseCleanerContext();
	const { setBusyAction, toggleModal, deleteEntries, updateOption, refreshEntryCount, allRefreshEntryCount, setLoadEntryItem,
		pushBusyBulkTasks, popBusyBulkTasks, getEntries, setEntries, setSelectedUsedBy } = useDatabaseCleanerContext();

	const [ list, setList ] = useState(initialList || []);
	const [ counts, setCounts ] = useState(initialCounts || []);
	const [ listUsedBy, setListUsedBy] = useState(initialUsedBy || []);

	const updateList = useCallback((newList) => {
		setList(newList);
	}, []);

	const updateCounts = useCallback((newCounts) => {
		setCounts(newCounts);
	}, []);

	const updateUsedBy = useCallback((newUsedBy) => {
		setListUsedBy(newUsedBy);
	}, []);

	const memoizedMutateList = useCallback(() => {
		if (mutateList) mutateList()
	}, [mutateList]);

	const memoizedOnUpdateCounts = useCallback((newCounts) => {
		if (onUpdateCounts) onUpdateCounts(newCounts);
	}, [onUpdateCounts]);

	// Callbacks
	const onDeleteAllSuccess = useCallback((item, currentCounts) => {
		const baseCounts = currentCounts ?? counts;
		const newCount = [...baseCounts.filter(v => v.item !== item), { item, count: 0 }];
		memoizedOnUpdateCounts(newCount);
		return newCount;
	}, [counts, memoizedOnUpdateCounts]);

	const onCleanStyleChangedSuccess = useCallback(() => memoizedMutateList(), [memoizedMutateList])

	const onRefreshSuccess = useCallback((item, response) => {
		const newCounts = counts.filter(v => v.item !== item);
		memoizedOnUpdateCounts([...newCounts, { item, count: response.data }]);
	}, [counts, memoizedOnUpdateCounts]);

	const onAllRefreshSuccess = useCallback((newCounts) => {
		memoizedOnUpdateCounts([...newCounts]);
	}, [memoizedOnUpdateCounts])

	// Actions
	const onDeleteAll = useCallback(async ({ item, currentCounts }) => {
		setBusyAction([...busyAction, { item: deleteBusyValue(item) }]);
		const baseCounts = currentCounts ?? counts;
		const count = baseCounts.find(v => v.item === item)?.count ?? 0;
		const times = Math.ceil(count / bulkBatchSize);
		const itemsTasks = [];
		for (let index = 0; index < times; index++) {
			const task = async (signal) => {
				await takeNap(delay);
				const result = await deleteEntries({ item, signal });
				if (result.data.finished && index + 1 < times) {
					bulkTasks.setAlwaysSkip();
				}
				return result;
			}
			itemsTasks.push(task);
		}
		pushBusyBulkTasks();
		await bulkTasks.start(itemsTasks);
		bulkTasks.reset();
		popBusyBulkTasks();

		let newCounts = [...baseCounts];
		if (onDeleteAllSuccess) {
			newCounts = onDeleteAllSuccess(item, currentCounts);
		} else {
			newCounts = [...baseCounts.filter(v => v.item !== item), { item, count: 0 }]
		}

		setBusyAction([...busyAction]);
		return newCounts;
	}, [counts, busyAction, bulkBatchSize, onDeleteAllSuccess, delay]);

	const onCleanStyleChanged = useCallback(async ({ param }) => {
		if (param.name === 'posts_metadata_duplicated_post_meta_clean_style' && param.value === 'auto') {
			alert('Based on the nature of meta duplicates, it is better to do it manually');
			return;
		}
		await updateOption({ param, onSuccess: onCleanStyleChangedSuccess, busyItem: section });
	}, [onCleanStyleChangedSuccess, updateOption, section]);

	const onRefresh = useCallback(async ({ item }) => {
		await refreshEntryCount({ item, onSuccess: onRefreshSuccess, busyItem: section })
	}, [onRefreshSuccess, section]);

	const onAllRefreshClick = useCallback(async (props = {}) => {
		const { exceptManual = null, customList = [] } = props;

		if (!list.length && !customList.length) {
			return false;
		}
		let currentCounts = [...counts];
		const targetList = customList.length ? customList : list;
		
		//const targetCleanStyle = exceptManual ? ['auto'] : ['auto', 'manual'];
		// Changed by Jordy: I feel like it's more natural to have the counts refreshed for Manual too,
		// some users (including me) was confused at some point. Let's try it.
 		const targetCleanStyle = ['auto', 'manual'];
		const autoLists = targetList.filter(v => targetCleanStyle.includes(v.clean_style));
		for (const data of autoLists) {
			currentCounts = await allRefreshEntryCount({ item: data.item, counts: currentCounts, onSuccess: onAllRefreshSuccess });
		}
		return true;
	}, [list, counts, onAllRefreshSuccess]);

	const onAllTrashClick = useCallback(async () => {
		let currentCounts = [...counts];
		const autoLists = list.filter(v => counts?.find(c => c.item === v.item)?.count > 0);
		for (const data of autoLists) {
			currentCounts = await onDeleteAll({ item: data.item, currentCounts });
		}
	}, [list, counts, onDeleteAll]);

	const disabledAllTrash = useMemo(() =>
		busyBulkTasks || !list.some(v => counts?.find(c => c.item === v.item)?.count > 0),
		[list, counts, busyBulkTasks]
	);

	// Table data
	const data = useMemo(() => {
		return sortIgnoreItemToEnd(list).map(v => {
			const isIgnore = v.clean_style === 'never';
			const itemCount = counts?.find(c => c.item === v.item)?.count ?? '-';
			const onDeleteAllClick = v.clean_style !== 'auto' ? () => toggleModal({ modal: 'deleteAll', enable: true, onNext: () => onDeleteAll({ item: v.item })}) : () => onDeleteAll({ item: v.item });
			const isRefreshBusy = busyAction.some(busy => busy.item === countBusyValue(v.item) || busy.item === deleteBusyValue(v.item));
			const showDeleteButton = itemCount > 0 && !busyAction.some(busy => busy.item === deleteBusyValue(v.item));
			const isLoadingEntries = busyAction.some(busy => busy.item === `loading_${v.item}`);
			const itemName = v.item.replace(/^(list_post_types_)/, "");
			const isUsedByBusy = busyAction.some(busy => busy.item === `${usedbyKey}${itemName}`);
			const details = (<div style={{ display: 'flex', alignItems: 'center' }}>
				<NameField item={v} itemText={v.name} />
				{!!v.info && !!v.infoType && <ItemInfoIcon info={v.info} infoType={v.infoType} />}
			</div>);
			const ratio = parseFloat(bulkTasks.value) / parseFloat(bulkTasks.max);
			const busyText = !isNaN(ratio) ? `${parseInt(Math.round(ratio * 100))}%` : '';
			const isActionBusy = isRefreshBusy || isUsedByBusy;

			const onEyeButtonClicked = async () => {
				setEntries([]);
				await setLoadEntryItem({ loadEntryItem: v.item, count: itemCount, page: 1 });
				toggleModal({ modal: 'loadEntry', enable: true, title: `${sections[section]}: ${v.name}`});
			}

			const count = isIgnore ? null : 
			<div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'space-between' }}>
				<span>{isNaN(ratio) ? itemCount : '-'}</span>
				<div style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 10 }}>
					{showDeleteButton && <NekoIcon onClick={onEyeButtonClicked} icon="search" color="#007cba"
						isBusy={isLoadingEntries} spinning={isLoadingEntries}
						style={{ width: 20, marginRight: -2, cursor: 'pointer' }} />}
					<NekoIcon onClick={() => onRefresh({ item: v.item }) } icon="replay" color="#007cba" spinning={isRefreshBusy}
						style={{ width: 20, cursor: 'pointer' }} />
				</div>
			</div>;

			const clean = (
				<div style={{ width: '90px' }}>
					<NekoSelect name={cleanOptionKey(v.item)} scrolldown={true}
						onChange={(value, name) => onCleanStyleChanged({ param: { name, value } })} value={v.clean_style}>
						{cleanStyles.map((v) => <NekoOption id={v.value} value={v.value} label={v.label} />)}
					</NekoSelect>
				</div>
			);

			const action = (!isIgnore &&
				<div style={{ display: 'flex' }}>
					<NekoButton className={'danger'}
						onClick={onDeleteAllClick} icon={isActionBusy ? '' : 'trash'}
						isBusy={isActionBusy} busyText={busyText} hideBusyIcon={false}
						disabled={!showDeleteButton || isRefreshBusy || busyBulkTasks || busyCleanBulkTasks || isUsedByBusy}
					>Clean</NekoButton>
				</div>
			);

			if (v.info) {
				const usedBy = <UsedByField item={v} itemName={itemName} itemsUsedBy={listUsedBy}
					optionName={usedbyKey} plugins={plugins} setSelectedUsedBy={setSelectedUsedBy}
					toggleModal={toggleModal} updateOption={updateOption} busy={isUsedByBusy}
					onRefresh={memoizedMutateList}
				/>;
				return { details, usedBy, count, clean, action };
			}

			return { details, count, clean, action };
		})
	}, [list, counts, listUsedBy, usedbyKey, busyAction, busyBulkTasks, busyCleanBulkTasks, bulkTasks,
		onDeleteAll, onCleanStyleChanged, onRefresh, plugins, updateOption, section]);

	return { data, onAllRefreshClick, onAllTrashClick, disabledAllTrash, updateList, updateCounts, updateUsedBy, memoizedMutateList };
}

export const useShiftSelectable = ({ pressShift, list, selectedList, setSelectedList, key }) => {

	const startIndex = useMemo(() => {
		if (!pressShift || !selectedList.length) return null;

		const last = selectedList[selectedList.length - 1];
		return list.findIndex(v => v[key] === last.name);
	}, [pressShift]);

	const onCheckboxChange = useCallback((isChecked, item) => {
		if (startIndex === null || !isChecked) {
			isChecked
				? setSelectedList([...selectedList, { name: item[key], info: item.info, args: item.args }])
				: setSelectedList([...selectedList.filter(option => option.name !== item[key])]);
			return;
		}
		const targetIndex = list.findIndex(v => v[key] === item[key]);
		const start = (startIndex < targetIndex ? startIndex : targetIndex) + 1;
		const end = startIndex < targetIndex ? targetIndex : startIndex;
		const betweenItems = list
			.slice(start, end)
			.map(v => { return { name: v[key], info: v.info } })
			.filter(v => !selectedList.some(s => s.name === v.name));

		setSelectedList([...selectedList, ...betweenItems, { name: item[key], info: item.info }]);
	}, [selectedList, startIndex, list]);

	return { onCheckboxChange };
}
